import { withStyles, Card, InterestAccount } from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { Title, Skeleton, Button } from 'UI';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useDialog } from '@/providers/Dialog.provider';
import { Balance } from '@/hooks/useApi/useAccountBalances';
import useAccountBalances from '@/hooks/useApi/useAccountBalances';
import useFetchInterestAccounts from '@/hooks/useApi/useFetchInterestAccounts';
import useFetchInterestAccountRates from '@/hooks/useApi/useFetchInterestAccountRates';
import useVirtualAccounts from '@/hooks/useApi/useVirtualAccounts';
import BalanceListItem from './BalanceListItem/BalanceListItem';
import BalanceDetails from './BalanceDetails/BalanceDetails';
import UpgradePlanDialog from './InterestAccounts/UpgradePlanDialog/UpgradePlanDialog';
import styles from './AccountSummaryPage.module.scss';
import InterestAccountBanner from './InterestAccounts/InterestAccountBanner/InterestAccountBanner';
import InterestAccountDrawer from './InterestAccounts/InterestAccountDrawer';
import AddVirtualAccountDialog from './AddVirtualAccountDialog';
import VirtualAccountInboundSimulationDialog from './VirtualAccountInboundSimulationDialog';
import { useDataFetchIndicator } from '@/hooks';

export type AccountType = 'disabled' | 'standard' | 'premium';
export type TransferType = 'Fund' | 'Withdraw';

const cn = withStyles(styles);

const AccountSummaryPage: FunctionComponent = () => {
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const dialog = useDialog();
  const { permissions } = usePermissions();
  const { response } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { balances, fetchBalances, fetchingBalances } = useAccountBalances();
  const [selectedBalance, setSelectedBalance] = useState<Balance | null>(null);
  const { interestAccounts, fetchInterestAccounts, fetchingInterestAccounts } = useFetchInterestAccounts();
  const { interestRates, fetchInterestRates } = useFetchInterestAccountRates();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [, setFetching] = useDataFetchIndicator();
  const [upgradePlanDialogOpen, setUpgradePlanDialogOpen] = useState(false);

  const { virtualAccounts, fetchVirtualAccounts, createVirtualAccount, simulateVirtualAccountCredit } =
    useVirtualAccounts();

  const interestAccountEnabled = useMemo(
    () => permissions.ViewInterestAccounts && response.InterestAccountEnabled,
    [permissions, response],
  );

  const standardAccount = useMemo<InterestAccount>(() => {
    return (
      interestAccounts.find((account) => account.InterestMaturityType === 'short-term') || {
        AccountName: '',
        CurrentBalance: 0,
        InterestAccrued: 0,
        EligibleBalance: 0,
        InterestMaturityType: 'short-term',
      }
    );
  }, [interestAccounts]);

  const premiumAccount = useMemo<InterestAccount | null>(() => {
    return interestAccounts.find((account) => account.InterestMaturityType === 'long-term') || null;
  }, [interestAccounts]);

  async function getInterestAccountRates() {
    if (!interestAccountEnabled) return;

    try {
      await fetchInterestRates();
    } catch (err) {
      if (err instanceof Error) {
        alertDialog.error(
          tGlobal('error'),
          t('fetchInterestRateError', {
            defaultValue: 'Something went wrong trying to fetch interest rates',
          }),
        );
      }
    }
  }

  async function getInterestAccounts() {
    try {
      await fetchInterestAccounts({});
    } catch (err) {
      if (err instanceof Error) {
        alertDialog.error(
          tGlobal('error'),
          t('fetchInterestAccountsError', { defaultValue: 'Something went wrong trying to fetch interest accounts' }),
        );
      }
    }
  }

  useEffect(() => {
    if (interestAccountEnabled) {
      getInterestAccounts();
      getInterestAccountRates();
    }

    if (response.VirtualAccountsEnabled) {
      fetchVirtualAccounts();
    }
  }, [interestAccountEnabled]);

  useEffect(() => {
    setSelectedBalance(balances.find((b) => b.Currency === selectedBalance?.Currency) || balances[0]);
  }, [balances]);

  const handleAddVirtualAccount = async () => {
    const result = await dialog.open(AddVirtualAccountDialog);

    if (result) {
      try {
        setFetching(true);
        await createVirtualAccount({ Currency: result });
        await Promise.all([fetchVirtualAccounts(), fetchBalances()]);

        toast.success(
          t('AddVirtualAccountDialog.createSuccess', {
            currency: result,
            defaultValue: '{{currency}} Account Created Successfully',
          }),
        );
      } catch (error) {
        if (error instanceof Error) {
          alertDialog.error(tGlobal('error'), error.message);
        } else {
          alertDialog.error(
            tGlobal('error'),
            t('AddVirtualAccountDialog.createError', { defaultValue: 'Failed to create virtual account' }),
          );
        }
      } finally {
        setFetching(false);
      }
    }
  };

  const simulateInboundTransfer = async (virtualAccountID: string, currency: string) => {
    try {
      // Generate random amount between 1000 and 50000 with 2 decimal places
      const randomAmount = (Math.random() * 9000 + 1000).toFixed(2);
      setFetching(true);
      await simulateVirtualAccountCredit(virtualAccountID, randomAmount);

      // Show success modal
      setFetching(false);

      await dialog.open((props) => (
        <VirtualAccountInboundSimulationDialog {...props} currency={currency} amount={randomAmount} />
      ));

      setFetching(true);
      // Refresh virtual accounts to update balances
      await Promise.all([fetchVirtualAccounts(), fetchBalances()]);
    } catch (error) {
      if (error instanceof Error) {
        alertDialog.error(tGlobal('error'), error.message);
      } else {
        alertDialog.error(
          tGlobal('error'),
          t('VirtualAccountInboundSimulation.simulateError', { defaultValue: 'Failed to simulate inbound transfer' }),
        );
      }
    } finally {
      setFetching(false);
    }
  };

  // Export function for external use
  // @ts-ignore - exposing for testing/external use
  window.simulateInboundTransfer = simulateInboundTransfer;

  return (
    <div className={cn('accountSummaryPage')}>
      <header className={cn('header')}>
        <Title>{t('title', { defaultValue: 'Account Balances' })}</Title>

        {response.VirtualAccountsEnabled && (
          <Button iconLeft="add" colorScheme="primary" onClick={handleAddVirtualAccount}>
            {t('addButton', { defaultValue: 'Virtual Account' })}
          </Button>
        )}
      </header>

      <Card
        style={{
          dropShadow: '0px 10px 12px #2D2D2D1A',
          borderColor: '#2D2D2D1A',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          maxHeight: 'fit-content',
        }}
      >
        {interestAccountEnabled && standardAccount && (
          <div className={cn('interestAccountBannerContainer')}>
            <InterestAccountBanner
              standardAccount={standardAccount}
              premiumAccount={premiumAccount}
              interestRates={interestRates}
              onEnablePremiumClick={async () => {
                setUpgradePlanDialogOpen(true);
              }}
              fetchingInterestAccounts={fetchingInterestAccounts}
              onViewDetailsClick={() => {
                setDrawerOpen(true);
              }}
            />
          </div>
        )}

        <div className={cn('accountSummaryContainer')}>
          <section className={cn('left')} aria-label="balances">
            <ul className={cn('balances', 'fade', 'scrollbar')}>
              {fetchingBalances ? (
                <>
                  <Skeleton height="120px" />
                  <Skeleton height="120px" />
                  <Skeleton height="120px" />
                </>
              ) : (
                balances.map((b) => {
                  const virtualAccount = virtualAccounts.find((va) => va.Currency === b.Currency) || null;

                  return (
                    <li role="button" key={b.Currency} onClick={() => setSelectedBalance(b)}>
                      <BalanceListItem
                        accountType={b.Currency === 'CAD' ? getInterestAccountType() : 'disabled'}
                        balance={b}
                        virtualAccount={virtualAccount}
                        selected={selectedBalance?.Currency === b.Currency}
                        onSimulateInboundTransfer={simulateInboundTransfer}
                      />
                    </li>
                  );
                })
              )}
            </ul>
          </section>

          <section className={cn('right')}>
            <Card
              className={cn('detailsCard')}
              style={{
                padding: '0px',
                dropShadow: '0px 10px 12px #2D2D2D1A',
                borderColor: '#2D2D2D1A',
                backgroundColor: '#fbfbfb',
              }}
              aria-label={selectedBalance ? `details for ${selectedBalance.Currency}` : ''}
            >
              {fetchingBalances ? (
                <div
                  style={{
                    margin: '1rem',
                    height: 'stretch',
                  }}
                >
                  <Skeleton height="100%" />
                </div>
              ) : (
                selectedBalance && (
                  <div className={cn('detailsContainer', 'scrollbar')}>
                    <BalanceDetails
                      balance={selectedBalance}
                      virtualAccount={virtualAccounts.find((va) => va.Currency === selectedBalance?.Currency) || null}
                      onSimulateInboundTransfer={simulateInboundTransfer}
                    />
                  </div>
                )
              )}
            </Card>
          </section>
        </div>
      </Card>

      {selectedBalance && (
        <InterestAccountDrawer
          open={drawerOpen}
          interestAccounts={interestAccounts}
          interestRates={interestRates}
          currency="CAD"
          onClose={() => setDrawerOpen(false)}
          onTransfer={() => {
            getInterestAccounts();
            getInterestAccountRates();
          }}
        />
      )}

      <UpgradePlanDialog open={upgradePlanDialogOpen} onClose={() => setUpgradePlanDialogOpen(false)} />
    </div>
  );

  function getInterestAccountType() {
    if (!interestAccountEnabled) return 'disabled';

    if (premiumAccount) return 'premium';

    return 'standard';
  }
};

export default AccountSummaryPage;
